import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiRequest } from '@/lib/api-client';
import { pricingReadinessKey } from './use-pricing-readiness';

// One value driver's derived lifecycle figures: Initial (what we started
// with), Provisional (what we currently expect), Final (what is fixed —
// write-once). All decimals arrive as strings.
export interface CoreValueDriverState {
  initial: string | null;
  provisional: string | null;
  final: string | null;
  is_final: boolean;
  // True when this driver's current value still comes from the July backfill —
  // the deal's contract price or PLANNED tonnage. It shows in the grid but must
  // never prefill or recompute a money field. Per driver: recording the LME
  // says nothing about the price or the weight.
  backfill_only: boolean;
}

export interface CoreValuesSideState {
  side: 'Purchase' | 'Sale';
  // 0 means the BL predates the value log — every figure shows empty until
  // someone records values manually.
  record_count: number;
  lme: CoreValueDriverState;
  price: CoreValueDriverState;
  weight_mt: CoreValueDriverState;
  // From the newest record of the side's log.
  last_modified_at: string | null;
  last_modified_by: string | null;
}

export interface CoreValuesResponse {
  // Empty while the core-values flag is off.
  sides: CoreValuesSideState[];
}

export function coreValuesKey(blOrderId: string | undefined) {
  return ['bl-order-core-values', blOrderId] as const;
}

export function useCoreValues(blOrderId: string | undefined) {
  return useQuery<CoreValuesResponse>({
    queryKey: coreValuesKey(blOrderId),
    enabled: blOrderId !== undefined && blOrderId.length > 0,
    queryFn: () =>
      apiRequest<CoreValuesResponse>(
        `/bl-orders/${encodeURIComponent(blOrderId ?? '')}/core-values`,
      ),
  });
}

// Decimal values as strings; a field left out stays unchanged. Marking a
// driver final is write-once — the backend rejects any later change to it.
export interface AppendCoreValuesBody {
  side: 'Purchase' | 'Sale';
  lme?: string;
  price?: string;
  weight_mt?: string;
  lme_final?: boolean;
  price_final?: boolean;
  weight_final?: boolean;
  note?: string;
}

export function useAppendCoreValues(blOrderId: string) {
  const qc = useQueryClient();
  return useMutation<{ id: string }, Error, AppendCoreValuesBody>({
    mutationFn: (body) =>
      apiRequest<{ id: string }>(`/bl-orders/${encodeURIComponent(blOrderId)}/core-values`, {
        method: 'POST',
        body,
      }),
    onSuccess: (_created, body) => {
      void qc.invalidateQueries({ queryKey: coreValuesKey(blOrderId) });
      // Final flags feed the side's Provisional / Final label.
      void qc.invalidateQueries({ queryKey: pricingReadinessKey(blOrderId, body.side) });
    },
  });
}
